import { AnnouncementStatus } from '@/app/enums/AnnouncementStatus';
import { AnnouncementType } from '@/app/enums/AnnouncementType';
import { formatDateTimeToString } from '@/app/helpers/formatDateTimeToString';
import { type AnnouncementDto } from '@/app/models/AnnouncementDto';
import { type AnnouncementReceiptReportDto } from '@/app/models/AnnouncementReceiptReportDto';
import { type AnnouncementRecipientDto } from '@/app/models/AnnouncementRecipientDto';
import { type DataTableColumnDef } from '@/app/models/DataTableColumnDef';
import RowMenuDropdown, { type RowMenuDropdownAction } from '@/app/modules/widgets/Dropdown/RowMenuDropdown';
import AnnouncementStatusBadge from '@/app/pages/notifications/announcements/components/AnnouncementStatusBadge';
import ReceiptStatusBadge from '@/app/pages/notifications/announcements/components/ReceiptStatusBadge';
import {
  type AnnouncementSummary,
  type ManagedAnnouncementHandlers,
} from '@/app/pages/notifications/announcements/core/_models';

const dateCell = (value?: string) => (
  <span className="text-gray-600">{value ? formatDateTimeToString(value) : '—'}</span>
);

// O autor e quem recebe veem título e tipo do mesmo jeito; cada lista só muda o tipo da linha.
const titleColumn = <TRow extends AnnouncementSummary>(): DataTableColumnDef<TRow> => ({
  id: 'title',
  header: 'TÍTULO',
  accessorFn: (announcement) => announcement.title,
  cell: ({ row }) => <span className="text-gray-800 fw-bold">{row.original.title}</span>,
});

const typeColumn = <TRow extends AnnouncementSummary>(): DataTableColumnDef<TRow> => ({
  id: 'type',
  header: 'TIPO',
  accessorFn: (announcement) => announcement.type,
  cell: ({ row }) => <span className="text-gray-600">{row.original.type.description}</span>,
});

/** Rascunho se edita; publicado só muda vigência ou se encerra; para mudar o texto, duplica. */
export const managedAnnouncementActions = (
  announcement: AnnouncementDto,
  handlers: ManagedAnnouncementHandlers,
): RowMenuDropdownAction[] => {
  const view: RowMenuDropdownAction = { label: 'Visualizar', onClick: () => handlers.onView(announcement) };
  const duplicate: RowMenuDropdownAction = { label: 'Duplicar', onClick: () => handlers.onDuplicate(announcement) };
  const report: RowMenuDropdownAction = {
    label: 'Relatório de ciência',
    onClick: () => handlers.onReport(announcement.id),
  };

  if (announcement.status.value === AnnouncementStatus.DRAFT) {
    return [
      view,
      { label: 'Editar', onClick: () => handlers.onEdit(announcement) },
      { label: 'Publicar', onClick: () => handlers.onPublish(announcement) },
      duplicate,
      { label: 'Excluir', onClick: () => handlers.onRemove(announcement) },
    ];
  }
  if (announcement.status.value === AnnouncementStatus.PUBLISHED) {
    return [
      view,
      report,
      { label: 'Alterar vigência', onClick: () => handlers.onValidity(announcement) },
      { label: 'Encerrar', onClick: () => handlers.onClose(announcement) },
      duplicate,
    ];
  }
  return [view, report, duplicate];
};

export const managedAnnouncementColumns = (
  handlers: ManagedAnnouncementHandlers,
): DataTableColumnDef<AnnouncementDto>[] => [
  titleColumn(),
  typeColumn(),
  {
    id: 'status',
    header: 'SITUAÇÃO',
    accessorKey: 'status',
    cell: ({ row }) => <AnnouncementStatusBadge status={row.original.status} />,
  },
  {
    id: 'start_date',
    header: 'INÍCIO',
    accessorKey: 'start_date',
    cell: ({ row }) => dateCell(row.original.start_date),
  },
  { id: 'end_date', header: 'FIM', accessorKey: 'end_date', cell: ({ row }) => dateCell(row.original.end_date) },
  {
    id: 'created_by_user_name',
    header: 'CRIADO POR',
    accessorKey: 'created_by_user_name',
    enableSorting: false,
    cell: ({ row }) => <span className="text-gray-600">{row.original.created_by_user_name ?? '—'}</span>,
  },
  {
    id: 'actions',
    cell: ({ row }) => <RowMenuDropdown actions={managedAnnouncementActions(row.original, handlers)} row={row} />,
  },
];

export const receivedAnnouncementColumns: DataTableColumnDef<AnnouncementRecipientDto>[] = [
  titleColumn(),
  typeColumn(),
  {
    id: 'published_by_user_name',
    header: 'PUBLICADO POR',
    accessorKey: 'published_by_user_name',
    enableSorting: false,
    cell: ({ row }) => <span className="text-gray-600">{row.original.published_by_user_name ?? '—'}</span>,
  },
  {
    id: 'published_date',
    header: 'PUBLICADO EM',
    accessorKey: 'published_date',
    cell: ({ row }) => dateCell(row.original.published_date),
  },
  {
    id: 'receipt',
    header: 'SITUAÇÃO',
    enableSorting: false,
    cell: ({ row }) => <ReceiptStatusBadge status={row.original.receipt_status.value} />,
  },
];

export const receiptReportColumns = (
  announcementType?: AnnouncementType,
): DataTableColumnDef<AnnouncementReceiptReportDto>[] => {
  if (!announcementType) return [];

  const mandatory = announcementType === AnnouncementType.MANDATORY;
  const settledColumn: DataTableColumnDef<AnnouncementReceiptReportDto> = mandatory
    ? {
        id: 'acknowledged_date',
        header: 'CIÊNCIA EM',
        enableSorting: false,
        cell: ({ row }) => dateCell(row.original.acknowledged_date),
      }
    : {
        id: 'dismissed_date',
        header: 'DISPENSADO EM',
        enableSorting: false,
        cell: ({ row }) => dateCell(row.original.dismissed_date),
      };

  return [
    {
      id: 'user_name',
      header: 'USUÁRIO',
      enableSorting: false,
      cell: ({ row }) => <span className="text-gray-800 fw-bold">{row.original.user_name}</span>,
    },
    {
      id: 'user_role_name',
      header: 'PERFIL',
      enableSorting: false,
      cell: ({ row }) => {
        const userRoleLabel = row.original.user_role_name ?? '—';
        return <span className="text-gray-600">{userRoleLabel}</span>;
      },
    },
    {
      id: 'seen_date',
      header: 'VISTO EM',
      enableSorting: false,
      cell: ({ row }) => dateCell(row.original.seen_date),
    },
    settledColumn,
    {
      id: 'status',
      header: 'SITUAÇÃO',
      enableSorting: false,
      cell: ({ row }) => <ReceiptStatusBadge status={row.original.status.value} />,
    },
  ];
};
